/**
 * Labstep
 *
 * @module components/Experiment/Form/ShowEdit/State
 * @desc State showedit field
 */

import { EntityActionLock } from 'labstep-web/components/Entity/Action/Lock';
import { ExperimentActionBar } from 'labstep-web/components/Experiment/Action/Bar';
import ExperimentActionSaveAs from 'labstep-web/components/Experiment/Action/SaveAs';
import { ExperimentFormShowEditStateButtonEdit as ButtonEdit } from 'labstep-web/components/Experiment/Form/ShowEdit/State/ButtonEdit';
import { ExperimentFormShowEditStateEmptyState } from 'labstep-web/components/Experiment/Form/ShowEdit/State/EmptyState';
import { ExperimentFormShowEditStateExperimentActionEdit } from 'labstep-web/components/Experiment/Form/ShowEdit/State/ExperimentActionEdit';
import { ExperimentFormShowEditStatePrompts } from 'labstep-web/components/Experiment/Form/ShowEdit/State/Prompts';
import { ExperimentWorkflowTemplateWarning } from 'labstep-web/components/ExperimentWorkflowTemplate/Warning';
import ModalEdit from 'labstep-web/components/Modal/Edit';
import ToolbarRight from 'labstep-web/components/Protocol/Form/ShowEdit/State/ToolbarRight';
import { ProtocolFormShowEditStateWarningModal } from 'labstep-web/components/Protocol/Form/ShowEdit/State/WarningModal';
import { withProseMirror } from 'labstep-web/containers/ProseMirror';
import { ProseMirrorConflict } from 'labstep-web/core/ProseMirror/Conflict';
import ProseMirror from 'labstep-web/prosemirror/Container';
import React from 'react';
import type { ExperimentFormShowEditStateProps } from './types';
import { getOnClick } from './utils';

export const ExperimentFormShowEditState: React.FC<
  ExperimentFormShowEditStateProps
> = ({
  experiment,
  experimentWorkflow,
  actions,
  onChange,
  canEdit,
  debouncingStatus,
  cancelDebouncing,
  match,
  withActionBar,
  proseMirrorKey,
  conflict,
  forceRefresh,
  editing,
  setEditMode,
  setViewMode,
  topMenuClassName,
}) => {
  const toolbarRight = React.useMemo(
    () => (
      <ToolbarRight
        experimentWorkflow={experimentWorkflow}
        protocol={experiment}
        setViewMode={setViewMode}
      />
    ),
    [experiment, experimentWorkflow, setViewMode],
  );
  return (
    <>
      <ProseMirrorConflict
        conflict={conflict}
        debouncing={debouncingStatus!.isFetching}
        cancelDebouncing={cancelDebouncing}
        forceRefresh={forceRefresh}
      />
      <ProtocolFormShowEditStateWarningModal
        status={debouncingStatus}
      />
      <ExperimentFormShowEditStatePrompts
        status={debouncingStatus}
        conflict={conflict}
        experiment={experiment}
        experimentWorkflow={experimentWorkflow}
      />
      <ExperimentWorkflowTemplateWarning
        experimentWorkflow={experimentWorkflow}
      />
      <ModalEdit parent={experiment} />
      {withActionBar && (
        <ExperimentActionBar
          experiment={experiment}
          editAction={
            !editing ? (
              <ExperimentFormShowEditStateExperimentActionEdit
                experiment={experiment}
                onClick={setEditMode}
              />
            ) : undefined
          }
          actions={
            <>
              {!experimentWorkflow &&
                experiment.locked_at &&
                !experiment.hasEmptyAllowedActionsLock && (
                  <EntityActionLock
                    actionComponentProps={{
                      type: 'text',
                    }}
                    entityName={experiment.entityName}
                    id={experiment.idAttr}
                    locked={false}
                    body={{
                      allowed_actions_lock: [],
                      locked_at: undefined,
                    }}
                  />
                )}
              {actions}
              {!experimentWorkflow && (
                <ExperimentActionSaveAs
                  experiment={experiment}
                  disabled={!!debouncingStatus?.isFetching}
                />
              )}
            </>
          }
        />
      )}
      {experiment.state === null && !editing ? (
        <ExperimentFormShowEditStateEmptyState
          experiment={experiment}
          onClick={setEditMode}
        />
      ) : (
        <div
          id="experiment-state"
          onClick={
            !match && canEdit
              ? getOnClick(editing, setEditMode, setViewMode)
              : undefined
          }
          onDragEnter={setEditMode}
        >
          {!editing && !withActionBar && canEdit && (
            <ButtonEdit
              experimentWorkflow={experimentWorkflow}
              experiment={experiment}
              onClick={setEditMode}
            />
          )}
          <ProseMirror
            proseMirrorKey={proseMirrorKey}
            experimentWorkflow={experimentWorkflow}
            editable={!conflict && editing}
            entity={experiment}
            onChange={onChange}
            initialState={experiment.state}
            topMenuClassName={topMenuClassName}
            toolbarRight={toolbarRight}
          />
        </div>
      )}
    </>
  );
};

export default withProseMirror(ExperimentFormShowEditState);
